import { ROUTES, SITE_URL } from '@/lib/constants';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'CanIDeal',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.svg`,
    description:
      'Global Cannabis Market AI Marketplace - Free market intelligence, real-time indices, and compliant B2B marketplace for vendors, retailers, and brokers',
    // No placeholder phone/email: point to the real support route.
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      url: `${SITE_URL}${ROUTES.support}`,
      availableLanguage: 'English',
    },
    sameAs: ['https://linkedin.com/company/canideal', 'https://twitter.com/canideal'],
  };
}

export function generateWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'CanIDeal',
    url: SITE_URL,
    description: 'Global Cannabis Market AI Marketplace',
    // No SearchAction: the site has no search page of its own (a target on
    // another domain is invalid for the sitelinks search box).
  };
}

export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function generateWebPageSchema(
  title: string,
  description: string,
  url: string,
  breadcrumbs?: BreadcrumbItem[]
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url,
    ...(breadcrumbs && {
      breadcrumb: generateBreadcrumbSchema(breadcrumbs),
    }),
  };
}

export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

interface ArticleSchemaOptions {
  /** Byline organization (default: CanIDeal). */
  author?: { name: string; url?: string };
  articleBody?: string;
  articleSection?: string;
}

/**
 * Article structured data. Without a `publishedDate` the datePublished field is
 * omitted (never guessed); dateModified falls back to it when given.
 */
export function generateArticleSchema(
  title: string,
  description: string,
  url: string,
  publishedDate?: string,
  modifiedDate?: string,
  imageUrl?: string,
  options: ArticleSchemaOptions = {}
) {
  const modified = modifiedDate || publishedDate;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url,
    mainEntityOfPage: url,
    ...(imageUrl && { image: [imageUrl] }),
    ...(publishedDate && { datePublished: publishedDate }),
    ...(modified && { dateModified: modified }),
    author: {
      '@type': 'Organization',
      name: options.author?.name ?? 'CanIDeal',
      ...(options.author?.url && { url: options.author.url }),
    },
    publisher: {
      '@type': 'Organization',
      name: 'CanIDeal',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.svg`,
      },
    },
    ...(options.articleSection && { articleSection: options.articleSection }),
    ...(options.articleBody && { articleBody: options.articleBody }),
  };
}

export function generateProductSchema(name: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    url,
    brand: {
      '@type': 'Brand',
      name: 'CanIDeal',
    },
  };
}

export function generateLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'CanIDeal',
    description: 'Global Cannabis Market AI Marketplace',
    url: SITE_URL,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      url: `${SITE_URL}${ROUTES.support}`,
      availableLanguage: 'English',
    },
  };
}
